import {focusLandmarkPath} from "components/sheetOpener";

/** The landing's composer, found from the surface that holds it: the drop landmark, and the create card inside it once the flow has one. */
function composerOf(surface: ParentNode | null) {
	const composer = surface?.querySelector<HTMLElement>("[data-drop-container]") ?? null;
	return {composer, card: composer?.querySelector<HTMLElement>("[data-create-card]") ?? composer};
}

/**
 * Opens the composer's own file chooser from a press elsewhere on the landing (kitecorp/transferify-ui#3776), and
 * brings the composer into view so the picked files are seen to land. A browser opens a chooser only inside the
 * user's own gesture, so this runs in the click handler itself, never after an await or a render.
 *
 * The first `input[type=file]` is the FILES picker; the second carries `webkitdirectory` and picks a folder
 * (`home/components/PageDropLayer`). The gate over an unaccepted composer does not stop the pick, as it does not
 * stop a drop: the files wait behind the gate, which still asks for the acceptance.
 */
export function chooseComposerFiles(surface: ParentNode | null): boolean {
	const {composer, card} = composerOf(surface);
	card?.scrollIntoView({block: "nearest"});
	const picker = composer?.querySelector<HTMLInputElement>('input[type="file"]:not([webkitdirectory])') ?? null;
	picker?.click();
	return picker !== null;
}

/**
 * Brings a visitor who arrived for the composer to it: into view, with focus on the composer's landmark through
 * the programmatic tabindex `components/sheetOpener` gives a stranded reader, so the next Tab is its first control.
 *
 * The landmark rather than that first control, because the control does not outlive the arrival: the consent
 * gate's lazy chunk suspends the boundary holding the composer's controls about 100 ms after hydration, which
 * unmounts the request tab for 120 ms and takes the focus to `body` with it (measured 2026-10-26, the Halloween
 * arrival of kitecorp/transferify-ui#3776). The drop landmark is the one node of the composer that stays.
 */
export function bringComposerIntoView(surface: ParentNode | null): boolean {
	const {composer, card} = composerOf(surface);
	if (composer === null) return false;
	card?.scrollIntoView({block: "center"});
	return focusLandmarkPath([composer]);
}
